import React, { useEffect, useMemo, useState } from "react";
import {
  Award, CalendarClock, CheckCircle2, CreditCard, ExternalLink, Hotel,
  IndianRupee, Loader2, Newspaper, Plane, Plus, RefreshCw, Sparkles,
  TriangleAlert, X,
} from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";
import {
  getRewardIntelligence, getStatementRewards, refreshRewardIntelligence,
  upsertRewardSnapshot,
} from "../lib/api";

const points = value => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(value || 0));
const money = value => `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(value || 0))}`;
const currentFY = () => {
  const today = new Date();
  return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};
const fyLabel = year => `FY ${year}–${String(year + 1).slice(-2)}`;
const deadlineLabel = days => {
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `${days} days remaining`;
};
const calendarDate = value => {
  if (!value) return "—";
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return "—";
  return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
};
const daysFromToday = value => {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(year, month - 1, day);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

function Kpi({ label, value, detail, tone = "cyan", icon: Icon }) {
  const tones = {
    cyan: "text-cyan-300", green: "text-emerald-300",
    amber: "text-amber-300", red: "text-rose-300", violet: "text-violet-300",
  };
  return (
    <div data-tone={tone} className="semantic-card rounded-xl border p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[.16em] text-[var(--text-muted)]">{label}</span>
        {Icon && <Icon size={15} className={tones[tone]} />}
      </div>
      <div className={`semantic-value mt-3 font-mono text-2xl ${tones[tone]}`}>{value}</div>
      <div className="mt-1 text-[10px] text-[var(--text-muted)]">{detail}</div>
    </div>
  );
}

const rewardBrand = programme => {
  const identity = `${programme.account_name || ""} ${programme.institution || ""}`.toUpperCase();
  const brands = [
    { match: /INFINIA/, mark: "∞", label: "HDFC", tone: "border-amber-300/25 bg-amber-300/10 text-amber-200", Icon: CreditCard },
    { match: /BIZ BLACK/, mark: "B", label: "HDFC", tone: "border-zinc-300/20 bg-zinc-200/10 text-zinc-100", Icon: CreditCard },
    { match: /BIZGROW|BIZ GROW/, mark: "BG", label: "HDFC", tone: "border-sky-300/25 bg-sky-300/10 text-sky-200", Icon: CreditCard },
    { match: /ATLAS/, mark: "A", label: "AXIS", tone: "border-fuchsia-300/25 bg-fuchsia-300/10 text-fuchsia-200", Icon: CreditCard },
    { match: /EMERALD|EMERALDE|6004/, mark: "E", label: "ICICI", tone: "border-orange-300/25 bg-orange-300/10 text-orange-200", Icon: CreditCard },
    { match: /SCAPIA/, mark: "S", label: "SCAPIA", tone: "border-cyan-300/25 bg-cyan-300/10 text-cyan-200", Icon: CreditCard },
    { match: /MARRIOTT|BONVOY/, mark: "M", label: "BONVOY", tone: "border-rose-300/25 bg-rose-300/10 text-rose-200", Icon: Hotel },
    { match: /ACCOR/, mark: "A", label: "ALL", tone: "border-indigo-300/25 bg-indigo-300/10 text-indigo-200", Icon: Hotel },
    { match: /MAHARAJA|AIR INDIA/, mark: "AI", label: "MAHARAJA", tone: "border-red-300/25 bg-red-300/10 text-red-200", Icon: Plane },
    { match: /KRISFLYER|SINGAPORE AIR/, mark: "SQ", label: "KRISFLYER", tone: "border-yellow-300/25 bg-yellow-300/10 text-yellow-200", Icon: Plane },
    { match: /BRITISH AIRWAYS/, mark: "BA", label: "AVIOS", tone: "border-blue-300/25 bg-blue-300/10 text-blue-200", Icon: Plane },
    { match: /FINNAIR/, mark: "F", label: "FINNAIR+", tone: "border-blue-200/25 bg-blue-200/10 text-blue-100", Icon: Plane },
    { match: /IHG/, mark: "IHG", label: "ONE REWARDS", tone: "border-teal-300/25 bg-teal-300/10 text-teal-200", Icon: Hotel },
  ];
  return brands.find(brand => brand.match.test(identity)) || {
    mark: (programme.account_name || "R").split(/\s+/).map(word => word[0]).join("").slice(0, 2).toUpperCase(),
    label: programme.account_type === "credit_card" ? "CARD" : "REWARDS",
    tone: "border-violet-300/25 bg-violet-300/10 text-violet-200",
    Icon: programme.account_type === "credit_card" ? CreditCard : Award,
  };
};

function RewardBrandMark({ programme }) {
  const brand = rewardBrand(programme);
  const Icon = brand.Icon;
  return (
    <div className={`h-12 w-12 shrink-0 rounded-xl border ${brand.tone} flex flex-col items-center justify-center shadow-inner`}>
      <div className="flex items-center gap-0.5 font-semibold leading-none">
        <Icon size={11} strokeWidth={1.8} />
        <span className="text-[13px] tracking-tight">{brand.mark}</span>
      </div>
      <span className="mt-1 max-w-[42px] truncate text-[6px] font-semibold tracking-[.12em]">{brand.label}</span>
    </div>
  );
}

function RewardProgrammeCard({ programme, openDrawer }) {
  const latestEarned = programme.latest_statement_earned;
  const displayedActivity = latestEarned !== null && latestEarned !== undefined
    ? latestEarned
    : programme.earned > 0
      ? programme.earned
      : programme.statement_earned > 0
        ? programme.statement_earned
        : programme.transaction_points;
  const latestStatementLabel = programme.latest_statement_end
    ? new Date(programme.latest_statement_end).toLocaleDateString("en-IN", { month: "short", year: "numeric" })
    : null;
  return (
    <article className="rounded-xl border border-white/10 bg-white/[0.025] p-4 hover:border-violet-400/25 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <RewardBrandMark programme={programme} />
          <div className="min-w-0">
            <h3 className="font-semibold leading-snug">{programme.account_name}</h3>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1">{programme.institution || programme.account_type}</div>
          </div>
        </div>
        <button onClick={() => openDrawer(programme)} className="inline-flex items-center gap-1 rounded-md border border-violet-400/25 px-2.5 py-1.5 text-[10px] text-violet-200 hover:bg-violet-400/10"><Plus size={11} /> Statement summary</button>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-5">
        <div><div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">Confirmed balance</div><div className="font-mono text-xl text-emerald-300 mt-1">{programme.latest_snapshot ? `${points(programme.closing_balance)} ${programme.reward_unit || "points"}` : "—"}</div></div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
            {programme.inferred_movement ? "Inferred net movement" : latestEarned !== null && latestEarned !== undefined ? "Latest statement earned" : "FY point activity"}
          </div>
          <div className="font-mono text-xl text-violet-300 mt-1">
            {points(displayedActivity)}
          </div>
          {latestStatementLabel && <div className="text-[9px] text-[var(--text-muted)] mt-1">{latestStatementLabel}</div>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4 text-[10px]">
        <span className="rounded-full border border-white/10 px-2 py-1">{programme.activity_count} transactions</span>
        {programme.latest_statement_redeemed > 0 && <span className="rounded-full border border-amber-500/20 text-amber-300 px-2 py-1">{points(programme.latest_statement_redeemed)} disbursed</span>}
        {programme.statement_earned > 0 && <span className="rounded-full border border-violet-500/20 text-violet-300 px-2 py-1">{points(programme.earned || programme.statement_earned)} earned this FY</span>}
        {programme.inferred_movement && <span className="rounded-full border border-amber-500/20 text-amber-300 px-2 py-1">Balance delta · inferred</span>}
        {programme.estimated_value_inr !== null && <span className="rounded-full border border-emerald-500/20 text-emerald-300 px-2 py-1">{money(programme.estimated_value_inr)} estimated</span>}
        {programme.point_value_inr !== null && programme.point_value_inr !== undefined && <span title={programme.point_value_basis || ""} className="rounded-full border border-cyan-500/20 text-cyan-300 px-2 py-1">₹{programme.point_value_inr}/point reference</span>}
        {programme.lapsed > 0 && <span className="rounded-full border border-rose-500/20 text-rose-300 px-2 py-1">{points(programme.lapsed)} lapsed</span>}
      </div>
    </article>
  );
}

function BillingCalendarCard({ programme }) {
  const timing = programme.billing_timing || {};
  const payment = timing.payment || {};
  const hasProjection = timing.projection_status && timing.projection_status !== "unavailable";
  const sourceBased = timing.projection_status === "estimated_from_statement";
  const confirmedDays = daysFromToday(timing.latest_payment_due_date);
  const projectedDays = daysFromToday(timing.projected_payment_due_date);
  const status = payment.status || "unpaid";
  const cleared = status === "paid" || status === "no_payment_due";
  const danger = status === "overdue" || status === "due_soon" || status === "partially_paid";
  const statusLabel = {
    paid: "Paid", no_payment_due: "No payment due", overdue: "Overdue",
    due_soon: "Payment due soon", partially_paid: "Part payment received", unpaid: "Payment pending",
  }[status] || "Payment pending";
  const cardTone = cleared
    ? "border-emerald-400/35 from-emerald-500/[0.07] to-emerald-500/[0.025]"
    : danger ? "border-rose-400/40 from-rose-500/[0.09] to-rose-500/[0.025]"
      : "border-amber-400/25 from-amber-500/[0.06] to-amber-500/[0.02]";
  const panelTone = cleared
    ? "border-emerald-400/25 bg-emerald-400/[0.07] text-emerald-300"
    : danger ? "border-rose-400/30 bg-rose-400/[0.08] text-rose-300"
      : "border-amber-400/20 bg-amber-400/[0.06] text-amber-300";
  const countdownValue = cleared
    ? null
    : confirmedDays === null ? null : Math.abs(confirmedDays);
  const countdownCaption = confirmedDays === null
    ? "Due date unavailable"
    : confirmedDays < 0 ? "days overdue"
      : confirmedDays === 0 ? "due today"
        : "days left";
  const creditDays = projectedDays ?? timing.estimated_credit_days_if_spent_today;
  return (
    <article data-testid={`billing-card-${programme.id}`} data-payment-status={status} className={`rounded-xl border bg-gradient-to-br p-4 ${cardTone}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <RewardBrandMark programme={programme} />
          <div className="min-w-0">
            <h3 className="font-semibold leading-snug">{programme.account_name}</h3>
            <div className="mt-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {[programme.owner, programme.institution].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-1 text-[9px] uppercase tracking-wider ${panelTone}`}>{statusLabel}</span>
      </div>

      {timing.latest_statement_date ? (
        <div className={`mt-5 rounded-xl border px-4 py-5 ${panelTone}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[.18em] opacity-80">{statusLabel}</div>
              <div className="mt-2 font-mono text-3xl font-black">{money(payment.outstanding_amount)}</div>
              <div className="mt-1 text-[10px] text-[var(--text-muted)]">outstanding from {money(payment.amount_due)} statement total</div>
            </div>
            {cleared ? <CheckCircle2 size={28} /> : <TriangleAlert size={28} />}
          </div>
          <div className="mt-5 grid grid-cols-2 border-y border-white/10 py-4 text-center">
            <div className="border-r border-white/10 px-2">
              <div className="text-[9px] font-bold uppercase tracking-[.16em] opacity-65">Payment countdown</div>
              {cleared ? (
                <>
                  <div className="mt-2 font-mono text-4xl font-black uppercase leading-none tracking-tight">{status === "paid" ? "Paid" : "Clear"}</div>
                  <div className="mt-2 text-[9px] font-bold uppercase tracking-[.16em] opacity-75">{payment.paid_before_due_date ? "before due date" : "nothing outstanding"}</div>
                </>
              ) : countdownValue !== null ? (
                <>
                  <div className="mt-2 font-mono text-5xl font-black leading-none tracking-tight">{countdownValue}</div>
                  <div className="mt-2 text-[10px] font-black uppercase tracking-[.18em] opacity-85">{countdownCaption}</div>
                </>
              ) : (
                <div className="mt-3 text-xs font-bold uppercase tracking-[.12em]">{countdownCaption}</div>
              )}
            </div>
            <div className="px-2 text-cyan-300">
              <div className="text-[9px] font-bold uppercase tracking-[.16em] opacity-70">Credit days if spent today</div>
              <div className="mt-2 font-mono text-5xl font-black leading-none tracking-tight">{creditDays ?? "—"}</div>
              <div className="mt-2 text-[10px] font-black uppercase tracking-[.18em] opacity-85">credit days</div>
              {timing.projected_payment_due_date && <div className="mt-2 text-[9px] text-[var(--text-muted)]">Pay around {calendarDate(timing.projected_payment_due_date)}</div>}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div><div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">Due date</div><div className="mt-1 font-mono">{calendarDate(timing.latest_payment_due_date)}</div></div>
            <div><div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">Timing</div><div className="mt-1 font-mono">{cleared ? (payment.paid_before_due_date ? "Paid on time" : "Cleared") : confirmedDays === null ? "—" : deadlineLabel(confirmedDays)}</div></div>
          </div>
          {payment.paid_amount > 0 && <div className="mt-3 text-[10px]">Confirmed paid: {money(payment.paid_amount)}{payment.paid_on ? ` on ${calendarDate(payment.paid_on)}` : ""} · {payment.source === "cred_payment_confirmation" ? "CRED receipt" : payment.source === "gmail_payment_confirmation" ? "Gmail receipt" : "verified source"}</div>}
        </div>
      ) : (
        <div className="mt-5 rounded-lg border border-dashed border-white/10 p-4 text-xs text-[var(--text-muted)]">
          <div className="font-semibold text-amber-200">Payment timing unavailable</div>
          <div className="mt-1">{timing.latest_statement_date
            ? `Bill generated ${calendarDate(timing.latest_statement_date)}, but its payment due date was not found.`
            : "The statement generation date and payment due date have not been extracted yet."}</div>
        </div>
      )}

      <div className="mt-4 border-t border-white/[0.07] pt-3 text-[10px] text-[var(--text-muted)]">
        {timing.latest_payment_due_date ? (
          <div className="flex flex-wrap justify-between gap-2">
            <span>Last payment due date</span>
            <span className="font-mono text-white">{calendarDate(timing.latest_payment_due_date)} · statement confirmed</span>
          </div>
        ) : (
          <div>No confirmed payment due date has been extracted yet.</div>
        )}
        {timing.latest_amount_due !== null && timing.latest_amount_due !== undefined && (
          <div className="mt-2 flex flex-wrap justify-between gap-2">
            <span>Latest statement amount due</span>
            <span className="font-mono text-rose-300">{money(timing.latest_amount_due)}</span>
          </div>
        )}
        {timing.source_filename && <div className="mt-2 truncate" title={timing.source_filename}>Source: {timing.source_filename}</div>}
        {hasProjection && <div className="mt-2 text-amber-200/70">Projection uses {sourceBased ? "the observed statement-to-due interval" : "saved account settings"}; verify against the next statement.</div>}
      </div>
    </article>
  );
}

function SnapshotDrawer({ programme, close, saved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    account_id: programme.id, statement_start: today, statement_end: today,
    opening_balance: "", earned: "", redeemed: "", lapsed: "", adjustments: "",
    closing_balance: "", expiring_points: "", expiry_date: "", point_value_inr: "",
    notes: "", offer_title: "", offer_description: "", offer_expires_on: "",
    offer_confidence: "confirmed_statement",
  });
  const set = (key, value) => setForm(previous => ({ ...previous, [key]: value }));
  const numeric = value => value === "" ? 0 : Number(value);
  const expected = numeric(form.opening_balance) + numeric(form.earned) + numeric(form.adjustments)
    - numeric(form.redeemed) - numeric(form.lapsed);
  const difference = numeric(form.closing_balance) - expected;
  const submit = async () => {
    if (!form.statement_start || !form.statement_end) return toast.error("Add the statement period");
    if (numeric(form.expiring_points) > 0 && !form.expiry_date) return toast.error("Add the points expiry date");
    setBusy(true);
    try {
      const { offer_title, offer_description, offer_expires_on, offer_confidence, ...snapshot } = form;
      await upsertRewardSnapshot({
        ...snapshot,
        opening_balance: numeric(form.opening_balance), earned: numeric(form.earned),
        redeemed: numeric(form.redeemed), lapsed: numeric(form.lapsed),
        adjustments: numeric(form.adjustments), closing_balance: numeric(form.closing_balance),
        expiring_points: numeric(form.expiring_points),
        expiry_date: form.expiry_date || null,
        point_value_inr: form.point_value_inr === "" ? null : Number(form.point_value_inr),
        offers: offer_title.trim() ? [{
          title: offer_title.trim(), description: offer_description.trim(),
          expires_on: offer_expires_on || null, confidence: offer_confidence,
        }] : [],
      });
      toast.success("Reward statement snapshot saved");
      saved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save reward snapshot");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onMouseDown={event => event.target === event.currentTarget && close()}>
      <aside className="w-full max-w-xl h-full overflow-y-auto bg-[var(--bg-surface)] border-l border-white/10 p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[.2em] text-violet-300">Statement reward summary</div>
            <h2 className="text-2xl font-semibold mt-1">{programme.account_name}</h2>
            <p className="text-xs text-[var(--text-muted)] mt-1">Enter exactly what the card statement confirms.</p>
          </div>
          <button onClick={close} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-7">
          <Field label="Statement start" type="date" value={form.statement_start} onChange={value => set("statement_start", value)} />
          <Field label="Statement end" type="date" value={form.statement_end} onChange={value => set("statement_end", value)} />
          <Field label="Opening balance" value={form.opening_balance} onChange={value => set("opening_balance", value)} />
          <Field label="Points earned" value={form.earned} onChange={value => set("earned", value)} />
          <Field label="Points redeemed" value={form.redeemed} onChange={value => set("redeemed", value)} />
          <Field label="Points lapsed" value={form.lapsed} onChange={value => set("lapsed", value)} />
          <Field label="Adjustments (+/−)" value={form.adjustments} onChange={value => set("adjustments", value)} allowNegative />
          <Field label="Closing balance" value={form.closing_balance} onChange={value => set("closing_balance", value)} />
          <Field label="Expiring points" value={form.expiring_points} onChange={value => set("expiring_points", value)} />
          <Field label="Expiry date" type="date" value={form.expiry_date} onChange={value => set("expiry_date", value)} />
          <div className="col-span-2">
            <Field label="Estimated INR value per point (optional)" value={form.point_value_inr} onChange={value => set("point_value_inr", value)} step="0.01" />
          </div>
        </div>
        <label className="block mt-4 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
          Notes
          <textarea value={form.notes} onChange={event => set("notes", event.target.value)}
            className="input-dark w-full min-h-24 mt-1.5 resize-none" placeholder="Statement wording, redemption details, or expiry notes" />
        </label>
        <div className="mt-5 rounded-xl border border-white/10 p-4">
          <div className="text-xs font-medium">Offer printed on this statement <span className="text-[var(--text-muted)]">(optional)</span></div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <label className="col-span-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Offer title<input className="input-dark w-full mt-1.5" value={form.offer_title} onChange={event => set("offer_title", event.target.value)} placeholder="Bonus points, fee waiver, milestone voucher…" /></label>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Expires on<input type="date" className="input-dark w-full mt-1.5" value={form.offer_expires_on} onChange={event => set("offer_expires_on", event.target.value)} /></label>
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Confidence<select className="input-dark w-full mt-1.5" value={form.offer_confidence} onChange={event => set("offer_confidence", event.target.value)}><option value="confirmed_statement">Confirmed from statement</option><option value="promotional_text">Promotional text</option><option value="needs_review">Needs review</option></select></label>
            <label className="col-span-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Details<textarea className="input-dark w-full min-h-20 mt-1.5 resize-none" value={form.offer_description} onChange={event => set("offer_description", event.target.value)} /></label>
          </div>
        </div>
        <div className={`mt-5 rounded-lg border p-3 text-xs ${Math.abs(difference) <= .01 ? "border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-200" : "border-amber-500/25 bg-amber-500/[0.06] text-amber-200"}`}>
          <div className="flex items-center gap-2 font-medium">
            {Math.abs(difference) <= .01 ? <CheckCircle2 size={15} /> : <TriangleAlert size={15} />}
            Expected closing balance: {points(expected)}
          </div>
          {Math.abs(difference) > .01 && <div className="mt-1 opacity-80">Difference from entered closing balance: {points(difference)}</div>}
        </div>
        <div className="flex justify-end gap-3 mt-7">
          <button onClick={close} className="px-4 py-2 rounded-lg border border-white/10 text-xs">Cancel</button>
          <button disabled={busy} onClick={submit} className="px-4 py-2 rounded-lg bg-violet-400 text-slate-950 text-xs font-semibold inline-flex items-center gap-2">
            {busy && <Loader2 size={13} className="animate-spin" />} Save snapshot
          </button>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, value, onChange, type = "number", step = "1", allowNegative = false }) {
  return (
    <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
      {label}
      <input type={type} min={type === "number" && !allowNegative ? "0" : undefined} step={step}
        value={value} onChange={event => onChange(event.target.value)}
        className="input-dark w-full mt-1.5" />
    </label>
  );
}

export default function RewardsPage() {
  const [fiscalYear, setFiscalYear] = useState(currentFY());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [intelligence, setIntelligence] = useState(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(true);
  const [intelligenceRefreshing, setIntelligenceRefreshing] = useState(false);
  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await getStatementRewards(fiscalYear);
      setData(response.data);
    } catch (error) {
      const message = error?.response?.data?.detail || "Could not load rewards";
      setData(null);
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [fiscalYear]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadIntelligence = async () => {
    setIntelligenceLoading(true);
    try {
      const response = await getRewardIntelligence();
      setIntelligence(response.data);
      const refreshedAt = response.data?.last_refresh?.created_at;
      const stale = !refreshedAt || Date.now() - new Date(refreshedAt).getTime() > 24 * 60 * 60 * 1000;
      if (stale) {
        const refreshed = await refreshRewardIntelligence();
        setIntelligence(previous => ({
          ...(previous || {}),
          items: refreshed.data.items || [],
          last_refresh: refreshed.data.run,
        }));
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load rewards intelligence");
    } finally {
      setIntelligenceLoading(false);
    }
  };
  const refreshIntelligence = async () => {
    setIntelligenceRefreshing(true);
    try {
      const response = await refreshRewardIntelligence();
      setIntelligence(previous => ({
        ...(previous || {}),
        items: response.data.items || [],
        last_refresh: response.data.run,
      }));
      const count = response.data?.run?.item_count || 0;
      toast.success(`Rewards bulletin refreshed · ${count} leads found`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not refresh rewards intelligence");
    } finally {
      setIntelligenceRefreshing(false);
    }
  };
  useEffect(() => { loadIntelligence(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const chart = useMemo(() => (data?.months || []).map(month => ({
    name: month.label,
    points: (data?.programmes || []).reduce((sum, programme) => {
      const row = programme.months.find(item => item.key === month.key);
      return sum + Number(row?.points || 0);
    }, 0),
  })), [data]);
  const summary = data?.summary || {};
  const balancesByUnit = summary.balances_by_unit || {};
  const balanceDetail = Object.entries(balancesByUnit)
    .map(([unit, value]) => `${points(value)} ${unit}`)
    .join(" · ");
  const creditCardProgrammes = (data?.programmes || []).filter(programme =>
    String(programme.account_type || "").toLowerCase().includes("credit card")
  );
  const membershipProgrammes = (data?.programmes || []).filter(programme =>
    !String(programme.account_type || "").toLowerCase().includes("credit card")
  );
  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-5" data-testid="rewards-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[.22em] text-violet-300">Cards & loyalty</div>
          <h1 className="text-3xl font-semibold mt-1">Rewards</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">Statement-confirmed points, balances, expiries and offers—without guessing.</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input-dark min-w-36" value={fiscalYear} onChange={event => setFiscalYear(Number(event.target.value))}>
            {[currentFY(), currentFY() - 1, currentFY() - 2, currentFY() - 3].map(year => <option key={year} value={year}>{fyLabel(year)}</option>)}
          </select>
          <button onClick={load} className="p-2.5 rounded-lg border border-white/10 hover:bg-white/5" title="Refresh"><RefreshCw size={15} /></button>
        </div>
      </div>

      {loading && !data ? <div className="h-72 grid place-items-center"><Loader2 className="animate-spin text-violet-300" /></div> : (
        <>
          {loadError && (
            <div className="rounded-xl border border-rose-400/25 bg-rose-500/[0.06] px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-rose-200">Reward balances could not be loaded. Saved accounts have not been changed.</div>
              <button onClick={load} className="text-xs text-white border border-white/15 rounded-lg px-3 py-1.5 hover:bg-white/5">Try again</button>
            </div>
          )}
          <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
            <Kpi label="Points earned this FY" value={data ? points(summary.points_earned_fy ?? summary.earned) : "—"} detail="Statement summaries first · transaction rows only as fallback" icon={Award} />
            <Kpi label="Confirmed balances" value={data ? `${summary.snapshot_coverage || 0} programmes` : "—"} detail={balanceDetail || "Balances remain separate by reward currency"} tone="green" icon={CheckCircle2} />
            <Kpi label="Estimated value" value={data ? money(summary.estimated_value_inr) : "—"} detail="Only programmes with a saved point value" tone="violet" icon={IndianRupee} />
            <Kpi label="Expiring" value={data ? points(summary.expiring_points) : "—"} detail="Statement-confirmed expiry lots" tone="amber" icon={CalendarClock} />
            <Kpi label="Lapsed this FY" value={data ? points(summary.lapsed) : "—"} detail="Confirmed from reward summaries" tone="red" icon={TriangleAlert} />
          </div>

          <section className="rounded-xl border border-amber-400/20 bg-gradient-to-br from-amber-500/[0.055] to-violet-500/[0.025] overflow-hidden">
            <div className="p-5 border-b border-white/[0.07] flex flex-wrap items-start justify-between gap-4">
              <div className="flex gap-3">
                <div className="h-9 w-9 rounded-lg border border-amber-400/20 bg-amber-400/10 grid place-items-center">
                  <CalendarClock size={17} className="text-amber-300" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Your rewards action centre</h2>
                  <p className="text-xs text-[var(--text-muted)] mt-1">Live deadlines from your confirmed accounts. These recalculate every day without waiting for a bulletin refresh.</p>
                </div>
              </div>
              <span className="rounded-full border border-white/10 px-3 py-1.5 text-[10px] text-[var(--text-muted)]">
                {(intelligence?.alerts || []).filter(alert => alert.severity !== "later").length} actionable
              </span>
            </div>
            {(intelligence?.alerts || []).length ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3">
                {(intelligence.alerts || []).slice(0, 12).map((alert, index) => {
                  const urgent = ["overdue", "critical"].includes(alert.severity);
                  const warning = alert.severity === "warning";
                  return (
                    <article key={alert.id} className={`p-4 border-white/[0.07] ${index % 3 ? "xl:border-l" : ""} ${index >= 3 ? "border-t" : ""}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className={`rounded-full border px-2 py-1 text-[9px] uppercase tracking-wider ${
                          urgent ? "border-rose-500/25 text-rose-300" :
                          warning ? "border-amber-500/25 text-amber-300" :
                          "border-cyan-500/20 text-cyan-300"
                        }`}>{String(alert.kind).replaceAll("_", " ")}</span>
                        <span className={`font-mono text-[10px] ${urgent ? "text-rose-300" : warning ? "text-amber-300" : "text-cyan-300"}`}>
                          {deadlineLabel(Number(alert.days_remaining))}
                        </span>
                      </div>
                      <h3 className="font-medium text-sm mt-3">{alert.title}</h3>
                      <p className="text-[11px] leading-relaxed text-[var(--text-muted)] mt-1.5">{alert.detail}</p>
                      <div className="flex justify-between gap-3 mt-3 text-[10px] text-[var(--text-muted)]">
                        <span>{alert.owner || "Household"}</span>
                        <span>{new Date(`${alert.deadline}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center text-xs text-[var(--text-muted)]">No confirmed expiry, inactivity, tier or subscription deadlines are currently recorded.</div>
            )}
          </section>

          <div className="grid xl:grid-cols-[1.5fr_.8fr] gap-4">
            <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <div>
                <h2 className="text-lg font-semibold">Monthly point activity</h2>
                <p className="text-xs text-[var(--text-muted)] mt-1">Statement-earned totals where available; transaction points are used only as a fallback. This is not the available balance.</p>
              </div>
              <div className="h-64 mt-5">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart}>
                    <CartesianGrid stroke="rgba(255,255,255,.06)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: "#8190a8", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#8190a8", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={points} />
                    <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,.12)", borderRadius: 10 }} formatter={value => [points(value), "Points"]} />
                    <Bar dataKey="points" fill="#a78bfa" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
            <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <h2 className="text-lg font-semibold">Expiry watch</h2>
              <p className="text-xs text-[var(--text-muted)] mt-1">Upcoming statement-confirmed expiries.</p>
              <div className="space-y-2 mt-5">
                {(data?.programmes || []).filter(row => row.expiring_points > 0).map(row => (
                  <div key={row.id} className="rounded-lg border border-amber-500/20 bg-amber-500/[0.05] p-3">
                    <div className="flex justify-between gap-3"><span className="text-xs font-medium">{row.account_name}</span><span className="font-mono text-xs text-amber-300">{points(row.expiring_points)}</span></div>
                    <div className="text-[10px] text-[var(--text-muted)] mt-1">{row.latest_snapshot?.expiry_date ? new Date(row.latest_snapshot.expiry_date).toLocaleDateString("en-IN") : "Expiry date in snapshot history"}</div>
                  </div>
                ))}
                {!data?.programmes?.some(row => row.expiring_points > 0) && <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-xs text-[var(--text-muted)]">No expiry lots recorded yet.</div>}
              </div>
            </section>
          </div>

          <section className="rounded-xl border border-rose-400/20 bg-white/[0.02] p-5">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-rose-400/20 bg-rose-400/10">
                <CalendarClock size={17} className="text-rose-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Credit-card payment calendar</h2>
                <p className="mt-1 text-xs text-[var(--text-muted)]">Check the likely statement close and payment date before spending. Confirmed dates come from card statements; future dates and credit days are clearly marked estimates.</p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {creditCardProgrammes.map(programme => <BillingCalendarCard key={programme.id} programme={programme} />)}
              {!creditCardProgrammes.length && <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-xs text-[var(--text-muted)]">No credit-card accounts are configured.</div>}
            </div>
          </section>

          <section>
            <div>
              <h2 className="text-lg font-semibold">Reward programmes</h2>
              <p className="text-xs text-[var(--text-muted)] mt-1">Credit-card rewards and external loyalty memberships are kept separate because their points do not have a universal value.</p>
            </div>

            <div className="mt-5">
              <div className="flex items-center gap-3">
                <div className="text-[10px] uppercase tracking-[.18em] text-cyan-300">Credit-card rewards</div>
                <div className="h-px flex-1 bg-white/[0.07]" />
                <span className="text-[10px] text-[var(--text-muted)]">{creditCardProgrammes.length} accounts</span>
              </div>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3">
                {creditCardProgrammes.map(programme => (
                  <RewardProgrammeCard key={programme.id} programme={programme} openDrawer={setDrawer} />
                ))}
              </div>
            </div>

            <div className="mt-7">
              <div className="flex items-center gap-3">
                <div className="text-[10px] uppercase tracking-[.18em] text-violet-300">Membership programmes</div>
                <div className="h-px flex-1 bg-white/[0.07]" />
                <span className="text-[10px] text-[var(--text-muted)]">{membershipProgrammes.length} accounts</span>
              </div>
              <p className="text-[11px] text-[var(--text-muted)] mt-2">Airline miles, hotel rewards and other loyalty memberships.</p>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3">
                {membershipProgrammes.map(programme => (
                  <RewardProgrammeCard key={programme.id} programme={programme} openDrawer={setDrawer} />
                ))}
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-violet-400/20 bg-gradient-to-br from-violet-500/[0.055] to-cyan-500/[0.025] overflow-hidden">
            <div className="p-5 border-b border-white/[0.07] flex flex-wrap items-start justify-between gap-4">
              <div className="flex gap-3">
                <div className="h-9 w-9 rounded-lg border border-violet-400/20 bg-violet-400/10 grid place-items-center"><Newspaper size={17} className="text-violet-300" /></div>
                <div>
                  <h2 className="text-lg font-semibold">Offers & transfer intelligence</h2>
                  <p className="text-xs text-[var(--text-muted)] mt-1">Matches card, airline and hotel offers to programmes you own. External articles remain leads until official terms are verified.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {intelligence?.last_refresh?.created_at && <span className="hidden md:inline text-[10px] text-[var(--text-muted)]">Updated {new Date(intelligence.last_refresh.created_at).toLocaleString("en-IN")}</span>}
                <button onClick={refreshIntelligence} disabled={intelligenceRefreshing} className="inline-flex items-center gap-2 rounded-lg border border-violet-400/25 px-3 py-2 text-xs text-violet-200 hover:bg-violet-400/10 disabled:opacity-50">
                  <RefreshCw size={13} className={intelligenceRefreshing ? "animate-spin" : ""} />
                  Check now
                </button>
              </div>
            </div>
            {intelligenceLoading && !intelligence ? (
              <div className="h-32 grid place-items-center"><Loader2 size={18} className="animate-spin text-violet-300" /></div>
            ) : (intelligence?.items || []).length ? (
              <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:[&>*:nth-child(n+3)]:border-t divide-white/[0.07]">
                {(intelligence.items || []).slice(0, 12).map((item, index) => (
                  <article key={item.id} className={`p-4 hover:bg-white/[0.025] transition-colors ${index % 2 ? "lg:border-l border-white/[0.07]" : ""}`}>
                    <div className="flex flex-wrap items-center gap-2 text-[9px] uppercase tracking-wider">
                      <span className={`rounded-full border px-2 py-1 ${item.kind === "devaluation" || item.kind === "expiry" ? "border-rose-500/25 text-rose-300" : item.kind === "transfer" || item.kind === "offer" ? "border-amber-500/25 text-amber-300" : "border-cyan-500/20 text-cyan-300"}`}>{String(item.kind || "news").replaceAll("_", " ")}</span>
                      {item.personalised && <span className="rounded-full border border-emerald-500/20 px-2 py-1 text-emerald-300">Matches your accounts</span>}
                      <span className="text-[var(--text-muted)]">{item.source_name} · {item.confidence} confidence</span>
                    </div>
                    <a href={item.url} target="_blank" rel="noreferrer" className="group mt-3 flex items-start gap-2 font-medium leading-snug hover:text-violet-200">
                      <span>{item.title}</span><ExternalLink size={12} className="mt-1 shrink-0 opacity-50 group-hover:opacity-100" />
                    </a>
                    {(item.affected_accounts || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {item.affected_accounts.map(account => <span key={account.id} className="rounded-md bg-white/[0.04] border border-white/[0.07] px-2 py-1 text-[10px] text-[var(--text-secondary)]">{account.name}</span>)}
                      </div>
                    )}
                    <div className="flex flex-wrap justify-between gap-2 mt-3 text-[10px] text-[var(--text-muted)]">
                      <span>{item.official_confirmation_required ? "Lead only · official terms required" : "Official source"}</span>
                      {Number(item.potential_value_inr || 0) > 0 && <span className="font-mono text-emerald-300">Affected balance value up to {money(item.potential_value_inr)}</span>}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-xs text-[var(--text-muted)]">No relevant offer leads are currently available. The system checks automatically each day, and Check now runs an immediate scan.</div>
            )}
          </section>

          <section className="rounded-xl border border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/10"><h2 className="font-semibold">Recent point activity</h2><p className="text-xs text-[var(--text-muted)] mt-1">Directly traceable to approved statement transactions.</p></div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-white/[0.02]"><tr><th className="text-left p-3">Date</th><th className="text-left p-3">Programme</th><th className="text-left p-3">Description</th><th className="text-right p-3">Spend</th><th className="text-right p-3">Points</th></tr></thead>
                <tbody>{(data?.activity || []).slice(0, 100).map(row => {
                  const programme = data.programmes.find(item => item.id === row.account_id);
                  return <tr key={row.id} className="border-t border-white/[0.06] hover:bg-white/[0.02]"><td className="p-3 whitespace-nowrap">{new Date(row.transaction_date).toLocaleDateString("en-IN")}</td><td className="p-3 text-cyan-200">{programme?.account_name || row.account_id}</td><td className="p-3 max-w-md truncate">{row.cleaned_description}</td><td className="p-3 text-right font-mono">{money(row.debit || row.credit)}</td><td className={`p-3 text-right font-mono ${Number(row.reward_points) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{Number(row.reward_points) > 0 ? "+" : ""}{points(row.reward_points)}</td></tr>;
                })}</tbody>
              </table>
            </div>
          </section>

          {(data?.anomalies || []).length > 0 && (
            <section className="rounded-xl border border-amber-500/20 bg-amber-500/[0.035] overflow-hidden">
              <div className="p-4 border-b border-amber-500/15 flex gap-3">
                <TriangleAlert size={17} className="text-amber-300 shrink-0" />
                <div><h2 className="font-semibold text-amber-100">Reward rows isolated for review</h2><p className="text-xs text-[var(--text-muted)] mt-1">These values remain traceable to their statements but are excluded from totals because they look like shifted foreign amounts or repeated rows.</p></div>
              </div>
              <div className="overflow-x-auto max-h-72">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]"><tr><th className="text-left p-3">Date</th><th className="text-left p-3">Description</th><th className="text-left p-3">Reason</th><th className="text-right p-3">Printed value</th></tr></thead>
                  <tbody>{data.anomalies.map(row => <tr key={row.id} className="border-t border-amber-500/10"><td className="p-3 whitespace-nowrap">{new Date(row.transaction_date).toLocaleDateString("en-IN")}</td><td className="p-3 max-w-md truncate">{row.cleaned_description}</td><td className="p-3 text-amber-200">{String(row.reward_review_reason).replaceAll("_", " ")}</td><td className="p-3 text-right font-mono">{points(row.reward_points)}</td></tr>)}</tbody>
                </table>
              </div>
            </section>
          )}

          {(data?.programmes || []).some(programme => programme.offers?.length) && (
            <section>
              <div><h2 className="text-lg font-semibold">Statement offers</h2><p className="text-xs text-[var(--text-muted)] mt-1">Kept separate from confirmed balances and tagged by confidence.</p></div>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
                {data.programmes.flatMap(programme => (programme.offers || []).map(offer => (
                  <article key={`${offer.snapshot_id}-${offer.title}`} className="rounded-xl border border-violet-400/20 bg-violet-400/[0.04] p-4">
                    <div className="flex items-start justify-between gap-3"><div><div className="text-[10px] uppercase tracking-wider text-violet-300">{programme.account_name}</div><h3 className="font-semibold mt-1">{offer.title}</h3></div><Sparkles size={15} className="text-violet-300 shrink-0" /></div>
                    {offer.description && <p className="text-xs text-[var(--text-secondary)] mt-3">{offer.description}</p>}
                    <div className="flex flex-wrap gap-2 mt-4 text-[9px] uppercase tracking-wider">
                      <span className="rounded-full border border-white/10 px-2 py-1">{String(offer.confidence || "").replaceAll("_", " ")}</span>
                      {offer.expires_on && <span className="rounded-full border border-amber-500/20 text-amber-300 px-2 py-1">Expires {new Date(offer.expires_on).toLocaleDateString("en-IN")}</span>}
                    </div>
                  </article>
                )))}
              </div>
            </section>
          )}

          <section className="rounded-xl border border-violet-400/15 bg-violet-400/[0.035] p-4 flex gap-3">
            <Sparkles size={17} className="text-violet-300 shrink-0 mt-0.5" />
            <div><div className="text-xs font-medium">Offers are review-safe</div><div className="text-[11px] text-[var(--text-muted)] mt-1">Statement offers will appear only when saved as confirmed, promotional text, or needs review. Generic advertisements are never treated as guaranteed personal benefits.</div></div>
          </section>
        </>
      )}
      {drawer && <SnapshotDrawer programme={drawer} close={() => setDrawer(null)} saved={() => { setDrawer(null); load(); }} />}
    </div>
  );
}
